import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useCptModifiersAdmin,
  useCreateCptModifier,
  useUpdateCptModifier,
  useToggleCptModifierActive,
} from "../../../hooks/admin/useCptModifiers";
import { useBillingScenariosAdmin } from "../../../hooks/admin/useBillingScenarios";
import CptModifierForm, {
  CptModifierFormValues,
} from "../../../components/admin/cptmodifiers/CptModifierForm";
import { CptModifier } from "../../../services/admin/cptmodifiers.service";

interface CptModifierFilters {
  billingScenarioIds?: string[];
  status?: string[];
}

const ITEMS_PER_PAGE = 20;

export default function CptModifiersAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<CptModifier | null>(null);
  const [itemToToggle, setItemToToggle] = useState<CptModifier | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<CptModifierFilters>({});
  const [pendingFilters, setPendingFilters] = useState<CptModifierFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const {
    data: cptModifiers = [],
    isLoading,
    refetch,
  } = useCptModifiersAdmin();
  const { data: billingScenarios = [] } = useBillingScenariosAdmin();
  const createCptModifier = useCreateCptModifier();
  const updateCptModifier = useUpdateCptModifier();
  const toggleCptModifierActive = useToggleCptModifierActive();

  // Billing scenario options for filter
  const billingScenarioOptions = billingScenarios
    .filter((s: any) => s.isActive !== false)
    .map((s: any) => ({ value: s.id, label: s.name }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label));

  // Client-side filtering
  const filteredData = useMemo(() => {
    let result = [...cptModifiers];

    // Search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower) ||
          (item.billingScenario?.name || "").toLowerCase().includes(searchLower)
      );
    }

    // Billing scenario filter
    if (filters.billingScenarioIds?.length) {
      result = result.filter(
        (item) =>
          item.billingScenarioId &&
          filters.billingScenarioIds!.includes(item.billingScenarioId)
      );
    }

    // Status filter
    if (filters.status?.length) {
      result = result.filter((item) => {
        if (filters.status!.includes("active") && item.isActive) return true;
        if (filters.status!.includes("inactive") && !item.isActive) return true;
        return false;
      });
    }

    return result;
  }, [cptModifiers, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.billingScenarioIds && filters.billingScenarioIds.length > 0) ||
    (filters.status && filters.status.length > 0);

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: CptModifier) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: CptModifierFormValues) => {
    // Convert empty strings to null for optional fields
    const sanitizedValues = {
      ...values,
      billingScenarioId: values.billingScenarioId || null,
    };

    if (editing) {
      await updateCptModifier.mutateAsync({
        id: editing.id,
        ...sanitizedValues,
      });
    } else {
      await createCptModifier.mutateAsync(sanitizedValues);
    }
    closeFormModal();
  };

  const handleToggleActive = (item: CptModifier) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (itemToToggle) {
      await toggleCptModifierActive.mutateAsync({
        id: itemToToggle.id,
        isActive: !itemToToggle.isActive,
      });
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>CPT Modifiers</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage CPT billing modifiers applied to CPT codes based on
                billing scenarios
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by code, description, or billing scenario..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.billingScenarioIds &&
                      filters.billingScenarioIds.length > 0 && (
                        <Badge variant="light" color="cyan">
                          {filters.billingScenarioIds.length} Billing Scenario
                          {filters.billingScenarioIds.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    {filters.status && filters.status.length > 0 && (
                      <Badge variant="light" color="orange">
                        {filters.status.length} Status
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} modifier
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  CPT Modifiers ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No modifiers match the current filters"
                    : "No CPT modifiers found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Code</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Billing Scenario</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        {/* Code */}
                        <Table.Td>
                          <Text size="sm" fw={600} ff="monospace">
                            {item.code}
                          </Text>
                        </Table.Td>

                        {/* Description */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description}
                          </Text>
                        </Table.Td>

                        {/* Billing Scenario */}
                        <Table.Td>
                          {item.billingScenario ? (
                            <Tooltip
                              label={
                                item.billingScenario.description ||
                                "No description"
                              }
                              withArrow
                            >
                              <Badge
                                variant="light"
                                color="cyan"
                                style={{ cursor: "help" }}
                              >
                                {item.billingScenario.name}
                              </Badge>
                            </Tooltip>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>

                        {/* Status */}
                        <Table.Td style={{ width: 100 }}>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td style={{ width: 100 }}>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit CPT Modifier" : "Add New CPT Modifier"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <CptModifierForm
              key={editing?.id || "new"}
              initialValues={editing || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="cpt-modifier-form"
                loading={
                  createCptModifier.isPending || updateCptModifier.isPending
                }
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter CPT Modifiers"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Billing Scenarios"
              placeholder="Select billing scenarios..."
              data={billingScenarioOptions}
              value={pendingFilters.billingScenarioIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  billingScenarioIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <MultiSelect
              label="Status"
              placeholder="Select status..."
              data={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.status || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: values.length > 0 ? values : undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            itemToToggle?.isActive
              ? "Deactivate CPT Modifier"
              : "Activate CPT Modifier"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"} modifier{" "}
              <strong>{itemToToggle?.code}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivating this modifier will make it unavailable for billing.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleCptModifierActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
